import React from 'react';
import { CreditCard, Check, FileText } from 'lucide-react';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { toast } from 'sonner';

export const PaymentSection = ({
  formData, setFormData, price, isConsultation,
  installmentCount, monthlyValue, enrollmentFee, formatBRL, fmt, currency,
  paypalConfig, handlePaypalCreateOrder, handlePaypalApprove, processing,
}) => {
  const currencySymbol = currency === 'EUR' ? '€' : currency === 'USD' ? 'US$' : 'R$';
  const paymentMethods = currency !== 'BRL'
    ? [
        { value: 'paypal', label: `PayPal (${currencySymbol})`, icon: CreditCard }
      ]
    : [
        { value: 'credit_card', label: 'Cartão de Crédito', icon: CreditCard },
        { value: 'pix', label: 'PIX', icon: Check },
        { value: 'boleto', label: 'Boleto', icon: FileText },
        { value: 'paypal', label: 'PayPal', icon: CreditCard }
      ];

  return (
  <>
    {/* Pagamento */}
    <div>
      <h3 className="text-white font-semibold mb-4 flex items-center">
        <CreditCard className="mr-2 text-red-500" size={20} />
        Forma de Pagamento
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        {paymentMethods.map((method) => {
          const Icon = method.icon;
          return (
            <label
              key={method.value}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all text-center ${
                formData.payment_method === method.value
                  ? 'border-red-500 bg-red-500/10'
                  : 'border-zinc-700 bg-zinc-800 hover:border-red-500/50'
              }`}
            >
              <input
                type="radio"
                name="payment"
                value={method.value}
                checked={formData.payment_method === method.value}
                onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                className="sr-only"
              />
              <Icon className="mx-auto mb-2 text-red-500" size={24} />
              <div className="text-white text-sm">{method.label}</div>
            </label>
          );
        })}
      </div>

      {formData.payment_method === 'credit_card' && (
        <div>
          <Label className="text-white">Parcelas</Label>
          <select
            value={formData.installments}
            onChange={(e) => setFormData({ ...formData, installments: parseInt(e.target.value) })}
            className="w-full bg-zinc-800 text-white border-zinc-700 rounded-md p-2 mt-2"
          >
            {[1, 2, 3, 4, 5, 6, 10, 12].map(i => (
              <option key={i} value={i}>
                {i}x de R$ {(price / i).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                {i === 1 ? ' (à vista)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      {(formData.payment_method === 'pix' || formData.payment_method === 'boleto') && !isConsultation && (
        <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-4 text-sm text-gray-300" data-testid="monthly-payment-note">
          <div className="font-semibold text-white mb-1">Pagamento mensal</div>
          <p>
            O curso é parcelado em <strong className="text-white">{installmentCount}x de {formatBRL(monthlyValue)}</strong> (mensalidades),
            quitadas mensalmente via {formData.payment_method === 'pix' ? 'PIX' : 'Boleto'}.
            A matrícula de <strong className="text-white">{formatBRL(enrollmentFee)}</strong> é paga agora para efetivar sua inscrição.
          </p>
        </div>
      )}
      {formData.payment_method === 'paypal' && !isConsultation && (
        <div className="bg-zinc-800/60 border border-blue-700/50 rounded-lg p-4 text-sm text-gray-300" data-testid="paypal-note">
          <div className="font-semibold text-white mb-1">Pagamento único via PayPal</div>
          <p>
            Pagamento à vista do curso completo em <strong className="text-white">{fmt(price)}</strong> via PayPal.
            Após a aprovação, sua matrícula é confirmada automaticamente.
          </p>
        </div>
      )}
    </div>

    {formData.payment_method === 'paypal' && !isConsultation ? (
      <div data-testid="paypal-buttons-container">
        {paypalConfig?.configured ? (
          <PayPalScriptProvider
            options={{
              clientId: paypalConfig.client_id,
              currency: currency,
              intent: 'capture'
            }}
          >
            <PayPalButtons
              style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' }}
              createOrder={handlePaypalCreateOrder}
              onApprove={handlePaypalApprove}
              onError={() => toast.error('Ocorreu um erro com o PayPal. Tente novamente.')}
            />
          </PayPalScriptProvider>
        ) : (
          <div className="text-center text-gray-400 p-4 bg-zinc-800 rounded-lg">
            PayPal indisponível no momento.
          </div>
        )}
      </div>
    ) : (
      <Button
        type="submit"
        className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6"
        disabled={processing || isConsultation}
      >
        {processing
          ? 'Processando...'
          : isConsultation
            ? 'Sob consulta'
            : `Confirmar Matrícula (${formatBRL(enrollmentFee)})`}
      </Button>
    )}
  </>
  );
};

export default PaymentSection;
